import React from 'react';
import '../styles/MobileSelectedComps.css';
import { colour, formatterCompact, numberFormatter } from '../styles/GlobalStyles';
import { Arrow, Copy, House, MapBubble, Sparkle, TickHeavy, Wand } from '../assets/icons';
import { FilterChip, IconButton, Dropdown, SmallCompCard, Button, ExitScreen } from './';
import { getPriceField, recordEvent, setSelectedPropertyReportTransaction, setSelectedPropertyTransaction } from '../functions';

const getMinMaxPrice = (comps, selectedComps, selectedPanel) => {
    if (comps.length === 0) return { minPrice: null, maxPrice: null };

    const selectedPanelComps = selectedPanel === "all" 
        ? selectedComps 
        : selectedComps.filter(e => e.type === selectedPanel);

    let minPrice = Infinity;
    let maxPrice = -Infinity;

    comps.forEach(comp => {
        const compIndex = selectedPanelComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId);
        
        // Skip this iteration if compIndex is -1 (not found)
        if (compIndex === -1) return;

        const price = getPriceField(comp);

        if (price < minPrice) minPrice = price;
        if (price > maxPrice) maxPrice = price;
    });

    return { 
        minPrice: minPrice === Infinity ? null : minPrice, 
        maxPrice: maxPrice === -Infinity ? null : maxPrice 
    };
};

const getMinMaxPricePerSqFoot = (comps, selectedComps, selectedPanel) => {
    if (comps.length === 0) return { minPricePerSqFoot: null, maxPricePerSqFoot: null };

    const selectedPanelComps = selectedPanel === "all" 
        ? selectedComps 
        : selectedComps.filter(e => e.type === selectedPanel);

    let minPricePerSqFoot = Infinity;
    let maxPricePerSqFoot = -Infinity;

    comps.forEach(comp => {
        const compIndex = selectedPanelComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId);
        
        // Skip this iteration if compIndex is -1 (not found)
        if (compIndex === -1) return;
        const squareFootage = comp.propertyAttributes.livingSquareFootage;
        const price = getPriceField(comp);
        const pricePerSqFoot = price / squareFootage;

        // Exclude null values
        if (pricePerSqFoot === null) return;

        if (pricePerSqFoot < minPricePerSqFoot) minPricePerSqFoot = pricePerSqFoot;
        if (pricePerSqFoot > maxPricePerSqFoot) maxPricePerSqFoot = pricePerSqFoot;
    });

    return { 
        minPricePerSqFoot: minPricePerSqFoot === Infinity ? null : minPricePerSqFoot, 
        maxPricePerSqFoot: maxPricePerSqFoot === -Infinity ? null : maxPricePerSqFoot 
    };
};

const getMinMaxNovationDOM = (comps, selectedComps, selectedPanel) => {
    if (comps.length === 0) return { minDOM: null, maxDOM: null };

    const selectedPanelComps = selectedPanel === "all" 
        ? selectedComps 
        : selectedComps.filter(e => e.type === selectedPanel);

    let minDOM = Infinity;
    let maxDOM = -Infinity;

    comps.forEach(comp => {
        const compIndex = selectedPanelComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId);
        
        // Skip this iteration if compIndex is -1 (not found)
        if (compIndex === -1) return;

        const novationDOM = comp.novationDOM;

        // Exclude null values
        if (novationDOM === null) return;

        if (novationDOM < minDOM) minDOM = novationDOM;
        if (novationDOM > maxDOM) maxDOM = novationDOM;
    });

    return { 
        minDOM: minDOM === Infinity ? null : minDOM, 
        maxDOM: maxDOM === -Infinity ? null : maxDOM 
    };
};

function MobileSelectedComps(props) {
    const allSelectedComps = props.allSelectedComps;
    const setAllSelectedComps = props.setAllSelectedComps;
    const fullSelectedComps = props.fullSelectedComps;
    const toggleSelectedComp = props.toggleSelectedComp;
    const expanded = props.expanded;
    const setExpanded = props.setExpanded;
    const showScrollableImages = props.showScrollableImages;
    const openDetailedView = props.openDetailedView;
    const shareLink = props.shareLink;
    const selectedPanel = props.selectedPanel;
    const setSelectedPanel = props.setSelectedPanel;
    const compTypeDropdownOpen = props.compTypeDropdownOpen;
    const setCompTypeDropdownOpen = props.setCompTypeDropdownOpen;
    const closeDropdowns = props.closeDropdowns;
    const companyId = props.companyId;
    const property = props.property;
    const linkCopied = props.linkCopied;
    const readOnly = props.readOnly;
    const compTypeLegend = props.compTypeLegend;
    const isLoaded = props.isLoaded;
    const bestComps = props.bestComps;
    const innerWidth = props.innerWidth;
    const getARV = props.getARV;
    const arvLoading = props.arvLoading;
    const arvEstimate = props.arvEstimate;
    const getAsIs = props.getAsIs;
    const asIsEstimate = props.asIsEstimate;
    const mobileSelectedCompsBody = props.mobileSelectedCompsBody;
    // const onboardingStep = props.onboardingStep;
    // const nextOnboardingStep = props.nextOnboardingStep;
    const arvComps = allSelectedComps.filter(e => e.type === "arv");
    const novationComps = allSelectedComps.filter(e => e.type === "novation");
    const asIsComps = allSelectedComps.filter(e => e.type === "as-is");
    const dropdownOpenObject = compTypeDropdownOpen === null ? [] : allSelectedComps.filter(e => e.zpid === compTypeDropdownOpen)[0];
    const { minPrice, maxPrice } = getMinMaxPrice(fullSelectedComps, allSelectedComps, selectedPanel);
    const { minPricePerSqFoot, maxPricePerSqFoot } = getMinMaxPricePerSqFoot(fullSelectedComps, allSelectedComps, selectedPanel);
    const { minDOM, maxDOM } = getMinMaxNovationDOM(fullSelectedComps, allSelectedComps, selectedPanel);

    const options = [
        {
            label: `All (${allSelectedComps.length})`,
            value: "all",
            selected: selectedPanel === "all" ? true : false,
            rangeTitle: "Range",
            disabled: false
        },
        {
            label: `ARV (${arvComps.length})`,
            value: "arv",
            selected: selectedPanel === "arv" ? true : false,
            rangeTitle: "Range",
            disabled: arvComps.length === 0 ? true : false
        },
        {
            label: `As-is (${asIsComps.length})`,
            value: "as-is",
            selected: selectedPanel === "as-is" ? true : false,
            rangeTitle: "Range",
            disabled: asIsComps.length === 0 ? true : false
        },
        {
            label: `Novation (${novationComps.length})`,
            value: "novation",
            selected: selectedPanel === "novation" ? true : false,
            rangeTitle: "Range",
            disabled: novationComps.length === 0 ? true : false
        }
    ];

    const compTypeOptions = [
        {
            label: "ARV",
            value: "arv",
            selected: dropdownOpenObject.type === "arv" ? true : false
        },
        {
            label: "As-is",
            value: "as-is",
            selected: dropdownOpenObject.type === "as-is" ? true : false
        },
        {
            label: "Novation",
            value: "novation",
            selected: dropdownOpenObject.type === "novation" ? true : false
        }
    ];

    const results = [
        {
            label: selectedPanel === "as-is" ? "As-is Estimate" : "ARV Estimate",
            icon: <Sparkle fill={colour.gray06} className="" />,
            value: selectedPanel === "as-is" && asIsEstimate === null ? "N/A" : selectedPanel === "as-is" ? formatterCompact.format(asIsEstimate).replace(".00", "") : arvEstimate === null ? "N/A" : formatterCompact.format(arvEstimate).replace(".00", "")
        },
        {
            label: "Range",
            icon: null,
            value:  minPrice === maxPrice ? formatterCompact.format(minPrice).replace(".00", "") : innerWidth < 700 ? `${formatterCompact.format(minPrice).replace(".00", "")} - ${formatterCompact.format(maxPrice).replace(".00", "")}` : `${formatterCompact.format(minPrice).replace(".00", "")} - ${formatterCompact.format(maxPrice).replace(".00", "")}`
        },
        {
            label: "Price/sqft",
            icon: null,
            value: minPricePerSqFoot === maxPricePerSqFoot ? formatterCompact.format(minPricePerSqFoot).replace(".00", "") : `${formatterCompact.format(minPricePerSqFoot).replace(".00", "")} - ${formatterCompact.format(maxPricePerSqFoot).replace(".00", "")}`
        },
        {
            label: "Days on market",
            icon: null,
            value: minDOM === maxDOM ? numberFormatter.format(minDOM) : `${numberFormatter.format(minDOM)} - ${numberFormatter.format(maxDOM)}`
        }
    ];

    const toggleExpanded = () => {
        setExpanded(!expanded);

        if ( !expanded === false ) {
            setCompTypeDropdownOpen(null);
            recordEvent("close_wizard", {});
        }
        else {
            if ( mobileSelectedCompsBody.current !== null ) {
                setTimeout(() => {
                    mobileSelectedCompsBody.current.scrollTop = 0;
                }, 50)
            }
            recordEvent("open_wizard", {});
        }
    };

    const changeSelectedOption = (value) => {
        setSelectedPanel(value);
    };

    const toggleCompTypeDropdown = (zpid) => {
        setCompTypeDropdownOpen(compTypeDropdownOpen === zpid ? null : zpid);
    };

    const changeCompStatus = (item, index) => {
        const value = item.value;
        const zpid = dropdownOpenObject.zpid;

        const cloneAllComps = [...allSelectedComps];
        const compIndex = cloneAllComps.findIndex(e => e.zpid === zpid);
        cloneAllComps[compIndex].type = value;
        setAllSelectedComps(cloneAllComps);
        setCompTypeDropdownOpen(null);

        if ( companyId !== null ) {
            const colRef = "companies";
            const docRef = companyId;
            const subColRef = "properties";
            const subDocRef = property.uniqueIds.propertyId;
            setSelectedPropertyTransaction(colRef, docRef, subColRef, subDocRef, cloneAllComps);
            setSelectedPropertyReportTransaction("reports", docRef, subColRef, subDocRef, cloneAllComps);
        }

        const remainingCompsInSection = cloneAllComps.filter(e => e.type === selectedPanel).length;
        if ( remainingCompsInSection === 0 ) {
            setSelectedPanel("all");
        }
        recordEvent("change_comp_status", {
            zpid: zpid,
            value: value
        });
    };

    const getLabel = (item) => {
        const getType = allSelectedComps.filter(e => e.zpid === item.uniqueIds.zillowId)[0].type;
        if ( getType === null ) {
            return "Unspecified";
        }
        else {
            const getTypeObject = compTypeOptions.filter(e => e.value === getType)[0];
            return getTypeObject.label;
        }
    };

    const checkZPIDCategory = (zpid) => {
        const compIndex = allSelectedComps.findIndex(e => e.zpid === zpid);
        const compType = allSelectedComps[compIndex].type;
        if (selectedPanel === "all") {
            return true;
        }
        else {
            return compType === selectedPanel;
        }
    };

    const getCompTypeObject = (item) => {
        const zpid = item.uniqueIds.zillowId;
        const compIndex = allSelectedComps.findIndex(e => e.zpid === zpid);
        if ( compIndex === -1 ) {
            return null;
        }
        const compType = allSelectedComps[compIndex].type;
        const compTypeObject = compTypeLegend.filter(e => e.value === compType)[0];
        return compTypeObject;
    }

    const removeComp = (item) => {
        toggleSelectedComp(item);
    };

    return (
        <div className={"mobile-selected-comps-outer-container bg-colour-white desktop-none " + (expanded === true ? "mobile-selected-comps-expanded" : "")}>
            {
                compTypeDropdownOpen !== null &&
                <ExitScreen
                    clickScreen={() => closeDropdowns()}
                />
            }
            <div className="mobile-selected-comps-inner-container">
                <div className="mobile-selected-comps-title-row relative-container">
                    <IconButton
                        size="large"
                        icon={<Arrow fill={colour.gray06} className="selected-comps-mobile-arrow" />}
                        disabled={false}
                        clickFunc={() => toggleExpanded()}
                        standardIcon={false}
                        background={true}
                        bgType={"white"}
                        contextTooltip={false}
                        contextTooltipBG={null}
                        contextTooltipText={null}
                        customClass={"mobile-selected-comps-arrow-button"}
                    />
                    <h2 className="heading-small-bold colour-primary text-align-center">
                        Kept comps
                    </h2>
                </div>
                <div className="mobile-selected-comps-chips-row white-space-no-wrap">
                    {
                        options.map((item, index) =>
                            <FilterChip
                                label={item.label}
                                selected={item.selected}
                                index={index}
                                disabled={item.disabled}
                                leftIcon={null}
                                rightIcon={null}
                                func={() => changeSelectedOption(item.value)}
                                key={index}
                            />
                        )
                    }
                </div>
                {
                    fullSelectedComps.length > 0 &&
                    <div 
                        className="mobile-selected-comps-properties-container"
                        ref={mobileSelectedCompsBody}
                    >
                        {
                            fullSelectedComps.map((item, index) =>
                                (selectedPanel === "all" || checkZPIDCategory(item.uniqueIds.zillowId) === true) &&
                                <div 
                                    className="mobile-selected-comps-card-element"
                                    key={index}
                                >
                                    <SmallCompCard
                                        property={item}
                                        closeCompCard={null}
                                        selected={true}
                                        addSelectedComp={readOnly === true ? null : removeComp}
                                        overlay={true}
                                        showScrollableImages={showScrollableImages}
                                        slideshow={false}
                                        openDetailedView={openDetailedView}
                                        expanded={expanded}
                                        subjectPropertyObject={property}
                                        isLoaded={isLoaded}
                                        bestComp={bestComps.findIndex(e => e.uniqueIds.zillowId === item.uniqueIds.zillowId) !== -1 ? true : false}
                                    />
                                    {
                                        readOnly === false ?
                                        <Dropdown
                                            open={compTypeDropdownOpen === item.uniqueIds.zillowId ? true : false}
                                            icon={null}
                                            label={getLabel(item)}
                                            disabled={false}
                                            options={compTypeOptions}
                                            multiselect={false}
                                            toggleDropdown={() => expanded === false ? null : toggleCompTypeDropdown(item.uniqueIds.zillowId)}
                                            cta={(item, index) => expanded === false ? null : changeCompStatus(item, index)}
                                            sidealign={"left"}
                                            topalign={"top"}
                                            indexSelect={false}
                                            selectedIndex={null}
                                        />
                                        :
                                        getCompTypeObject(item) !== null ?
                                        <div className="run-comps-best-comps-title-container">
                                            <div className="relative-container">
                                                <MapBubble
                                                    className=""
                                                    fill={getCompTypeObject(item).fill}
                                                    stroke={getCompTypeObject(item).stroke}
                                                    strokeWidth="0.5"
                                                />
                                                <House
                                                    fill={getCompTypeObject(item).house}
                                                    className="all-comps-map-house-icon"
                                                />
                                            </div>
                                            <span className="body-bold colour-primary">
                                                {getCompTypeObject(item).label.slice(0, -1)}
                                            </span>
                                        </div>
                                        :
                                        null
                                    }
                                </div>
                            )
                        }
                    </div>
                }
            </div>
            {
                expanded === true &&
                <div className="mobile-selected-comps-footer-container bg-colour-white">
                    <div className="mobile-selected-comps-footer-inner-container">
                        <div className="mobile-selected-comps-results-container">
                            {
                                results.map((item, index) =>
                                    <div 
                                        className="mobile-selected-comps-results-element"
                                        key={index}
                                    >
                                        <div className="mobile-selected-comps-results-element-title">
                                            {
                                                item.icon !== null &&
                                                item.icon
                                            }
                                            <h3 className="body-bold colour-primary">
                                                {item.label}
                                            </h3>
                                        </div>
                                        <span className="body-regular colour-primary">
                                            {item.value}
                                        </span>
                                    </div>
                                )
                            }
                            <div className="mobile-selected-comps-arv-button">
                                {
                                    getARV !== null && arvComps.length >= 2 && readOnly === false && (selectedPanel === "all" || selectedPanel === "arv") ?
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text={"Get ARV"}
                                        cta={() => getARV()}
                                        disabled={false}
                                        leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={arvLoading}
                                    />
                                    :
                                    getAsIs !== null && asIsComps.length >= 2 && readOnly === false && selectedPanel === "as-is" ?
                                    <Button
                                        buttonType="primary-button"
                                        size="large"
                                        text={"Get As-is"}
                                        cta={() => getAsIs()}
                                        disabled={false}
                                        leftIcon={arvLoading === true ? null : <Wand fill={colour.gray00} className="selected-comps-wand" />}
                                        leftIconClass=""
                                        rightIcon={null}
                                        rightIconClass=""
                                        alt=""
                                        standardIcon={false}
                                        badge={false}
                                        badgeVal={null}
                                        badgeBG={null}
                                        buttonRef={null}
                                        spinner={arvLoading}
                                    />
                                    :
                                    null
                                }
                            </div>
                            <div className="mobile-selected-comps-arv-button">
                                <Button
                                    buttonType="secondary-button"
                                    size="large"
                                    text={linkCopied === true ? "Copied" : "Share"}
                                    cta={() => shareLink()}
                                    disabled={false}
                                    leftIcon={linkCopied === true ? <TickHeavy fill={colour.gray06} className="" /> : <Copy fill={colour.gray06} className="" />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
};

export default MobileSelectedComps;